import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/component/Toast';
import { captureException } from '@/feature/sentry';
import { useTexts } from '../lang';
import { ConversationBaseDto, ConversationReferenceDto } from './api.types';
import { ChatMessage } from './types';
import { ChatContext } from './useChatContext';

const createApiError = (message: string, response: Response) =>
  new Error(message, { cause: { response } });

export const createStreamedResponseHandler = (
  chatContext: ChatContext,
  setConversation: (conversation: ConversationBaseDto) => void,
  setMessage: (message: Partial<ChatMessage>) => void,
  onDone: () => void,
  onError: (error: any) => void
) => {
  return async (apiResponse: Response) => {
    try {
      if (!apiResponse.body) {
        throw createApiError(
          'No response body received from conversation api',
          apiResponse
        );
      }
      if (!apiResponse.ok) {
        throw createApiError(
          `Unexpected response (http ${apiResponse.status}) received from conversation api`,
          apiResponse
        );
      }
      const temporaryId = crypto.randomUUID();
      const reader = apiResponse.body.getReader();
      const decoder = new TextDecoder('utf-8');

      let isReferenceEvent = false;
      let responseFragment: any = undefined;
      const references: ConversationReferenceDto[] = [];
      let lastLine = '';

      while (true) {
        const { value, done } = await reader.read();

        let rawLines: string[] = [];

        if (!done) {
          const decodedText =
            lastLine + decoder.decode(value, { stream: true });
          // The expeced decoded text will be any of the following type of lines:
          // data:{"text":" * Markdown text fragment"}
          // data:{"id":uuid,"messageId":uuid,"name":"Name of conversation"}"
          // event:reference
          // data:{"topicId":"manuals|ch550","documentId":uuid,"documentName":"","documentUrl":pdf url,"pages":[176,177,267],"mostRelevantPage":177}
          const tmpLines = decodedText.split('\n');
          // The chunks returned by reader.read() is normally, but not always, divided between complete lines.
          // Therefore we must wait with processing the last line in each chunk until the next chunk.
          lastLine = tmpLines.pop() || '';
          rawLines = tmpLines.filter((line) => line);
        } else if (lastLine) {
          rawLines = [lastLine];
          lastLine = '';
        }

        if (rawLines.length) {
          for (let i = 0; i < rawLines.length; i += 1) {
            const textRow = rawLines[i];
            const eventName = textRow.startsWith('event:')
              ? textRow.slice(6)
              : undefined;

            if (eventName) {
              if (eventName === 'reference') {
                isReferenceEvent = true;
              } else {
                console.warn('Received unknown conversation event: ' + textRow);
                isReferenceEvent = false;
              }
              continue;
            }

            const jsonData = textRow.startsWith('data:')
              ? textRow.slice(5)
              : undefined;

            if (!jsonData) {
              console.warn('Received unknown conversation content: ' + textRow);
              continue;
            }

            const lineData = JSON.parse(jsonData);

            if (isReferenceEvent) {
              references.push(lineData);
            } else {
              responseFragment = {
                ...responseFragment,
                ...lineData,
                timestamp: responseFragment?.timestamp ?? new Date(),
                text: (responseFragment?.text ?? '') + (lineData.text ?? ''),
              };
            }
          }
        }

        const messageId = responseFragment?.messageId ?? temporaryId;
        if (responseFragment) {
          setMessage({
            id: messageId,
            temporaryId,
            sender: 'assistant',
            content: responseFragment.text,
            timestamp: responseFragment.timestamp,
            isStreaming: !done,
          });
        }

        if (responseFragment?.id) {
          setConversation({
            id: responseFragment.id,
            name: responseFragment.name,
            equipmentId: chatContext.equipmentId!,
          });
        }

        if (references.length) {
          setMessage({
            id: messageId + '_ref',
            sender: 'assistant',
            isReference: true,
            references,
            isStreaming: !done,
          });
        }

        if (done) {
          onDone();
          break;
        }
      }
    } catch (error) {
      onError(error);
    }
  };
};

export const useStreamedResponseHandler = (
  chatContext: ChatContext,
  setConversation: (conversation: ConversationBaseDto) => void,
  setMessage: (message: Partial<ChatMessage>) => void
) => {
  const queryClient = useQueryClient();
  const onDone = () => queryClient.invalidateQueries({ queryKey: ['chatbot'] });
  const toast = useToast();
  const texts = useTexts().component.chatbot.error;
  const onError = (error: any) => {
    captureException(error);
    const httpStatusCode = error?.cause?.response?.status;
    // httpStatusCode may be undefined, as we are catching all types of exceptions.
    switch (httpStatusCode) {
      case 429:
        toast.error(texts.requestQuotaExceeded);
        break;
      default:
        toast.error(texts.technicalError);
    }
  };

  return createStreamedResponseHandler(
    chatContext,
    setConversation,
    setMessage,
    onDone,
    onError
  );
};
